"use client";

import { useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  BUCKET_FILTERS,
  DEFAULT_SORT,
  PRIZE_CATEGORIES,
  RANGE_FILTERS,
  SORT_OPTIONS,
  type BucketKey,
  type PrizeCategory,
  type RangeKey,
  type SortKey,
} from "../lifestyle-constants";
import type { LifestyleFilterState, RangeValue } from "./useLifestyleFilters";

/** Long enough to swallow a slider drag, short enough to feel instant. */
const DEBOUNCE_MS = 300;

const RANGE_PARAMS: RangeKey[] = RANGE_FILTERS.map(filter => filter.key);

function parseRange(raw: string | null): RangeValue | null {
  if (!raw) return null;
  const [min, max] = raw.split("-").map(Number);
  if (!Number.isFinite(min) || !Number.isFinite(max) || min > max) return null;
  return { min, max };
}

function parseList(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map(value => value.trim())
    .filter(Boolean);
}

/** Reads filter state out of a query string. Exported for tests. */
export function readFiltersFromSearch(search: string): {
  filters: Partial<LifestyleFilterState>;
  date: string | null;
} {
  const params = new URLSearchParams(search);
  const filters: Partial<LifestyleFilterState> = {};

  const category = params.get("category");
  if (category && (PRIZE_CATEGORIES as readonly string[]).includes(category)) {
    filters.category = category as PrizeCategory;
  }

  const keyword = params.get("q");
  if (keyword) filters.keyword = keyword;

  const sort = params.get("sort");
  if (sort && SORT_OPTIONS.some(option => option.key === sort)) {
    filters.sort = sort as SortKey;
  }

  const makes = parseList(params.get("makes"));
  if (makes.length > 0) filters.makes = makes;

  const styles = parseList(params.get("styles"));
  if (styles.length > 0) filters.styles = styles;

  const ranges: Partial<Record<RangeKey, RangeValue>> = {};
  for (const key of RANGE_PARAMS) {
    const range = parseRange(params.get(key));
    if (range) ranges[key] = range;
  }
  if (Object.keys(ranges).length > 0) filters.ranges = ranges;

  // Unknown band ids are dropped rather than trusted, so an edited or stale
  // link cannot put the drawer into a state its checkboxes cannot represent.
  const buckets: Partial<Record<BucketKey, string[]>> = {};
  for (const definition of BUCKET_FILTERS) {
    const ids = parseList(params.get(definition.key)).filter(id => definition.buckets.some(bucket => bucket.id === id));
    if (ids.length > 0) buckets[definition.key] = ids;
  }
  if (Object.keys(buckets).length > 0) filters.buckets = buckets;

  const date = params.get("date");

  return { filters, date: date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null };
}

/** Serialises filter state, omitting anything still at its default. */
export function buildSearch(filters: LifestyleFilterState, date: string | null): string {
  const params = new URLSearchParams();

  if (filters.category) params.set("category", filters.category);
  if (filters.keyword.trim()) params.set("q", filters.keyword.trim());
  if (filters.sort !== DEFAULT_SORT) params.set("sort", filters.sort);
  if (date) params.set("date", date);
  if (filters.makes.length > 0) params.set("makes", filters.makes.join(","));
  if (filters.styles.length > 0) params.set("styles", filters.styles.join(","));

  for (const key of RANGE_PARAMS) {
    const range = filters.ranges[key];
    if (range) params.set(key, `${range.min}-${range.max}`);
  }

  for (const definition of BUCKET_FILTERS) {
    const ids = filters.buckets[definition.key];
    if (ids && ids.length > 0) params.set(definition.key, ids.join(","));
  }

  return params.toString();
}

/** Normalises the current location search for stable comparisons. */
export function readCurrentSearch(): string {
  return new URLSearchParams(window.location.search).toString();
}

/**
 * Builds a same-document URL for `replaceState`.
 *
 * Mobile Safari and in-app WebViews reject absolute URLs here; the rest of the
 * site writes `pathname + search + hash` instead (see MasterReact.cshtml).
 */
export function buildPathWithSearch(
  search: string,
  pathname = window.location.pathname,
  hash = window.location.hash
): string {
  return search ? `${pathname}?${search}${hash}` : `${pathname}${hash}`;
}

function writeSearchToUrl(search: string): void {
  const nextPath = buildPathWithSearch(search);
  window.history.replaceState(null, "", nextPath);
}

interface UseLifestyleUrlStateArgs {
  filters: LifestyleFilterState;
  selectedDate: string | null;
  hydrate: (state: Partial<LifestyleFilterState>) => void;
  setSelectedDate: (date: string | null) => void;
}

/**
 * Keeps the URL in step with the filters so a filtered view can be shared.
 *
 * Uses the History API rather than `next/navigation`: in production this page
 * ships as a webpack bundle mounted into a Razor view, where no Next router
 * exists. `replaceState` keeps the write shallow — filter churn must not
 * navigate or fill the back stack, so in-page back/forward through filter
 * states is deliberately unsupported.
 *
 * Replaces `updateUrlParameters`, `setFiltersFromUrlParameters`,
 * `getDateFromUrlParameter`, `setTabFromUrlParameter` and `clearUrlParameters`.
 */
export default function useLifestyleUrlState({
  filters,
  selectedDate,
  hydrate,
  setSelectedDate,
}: UseLifestyleUrlStateArgs) {
  const [isHydrated, setIsHydrated] = useState(false);
  const lastWritten = useRef<string>("");
  const lastDiscreteKey = useRef("");

  // Hydration runs on mount, which lands well before the prize requests
  // resolve, so a shared link never flashes an unfiltered result set.
  useLayoutEffect(() => {
    lastWritten.current = readCurrentSearch();

    const { filters: fromUrl, date } = readFiltersFromSearch(window.location.search);
    if (Object.keys(fromUrl).length > 0) hydrate(fromUrl);
    if (date) setSelectedDate(date);
    setIsHydrated(true);
    // Mount only: re-running would fight the user's subsequent changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search = useMemo(() => buildSearch(filters, selectedDate), [filters, selectedDate]);

  // Dropdowns and checkboxes commit immediately; typing and slider drags wait,
  // so dragging a range does not fire a replaceState per frame.
  const discreteKey = useMemo(
    () =>
      [
        filters.category,
        filters.sort,
        selectedDate,
        filters.keyword.trim(),
        filters.makes.join(","),
        filters.styles.join(","),
        JSON.stringify(filters.buckets),
      ].join("|"),
    [filters.category, filters.sort, filters.keyword, filters.makes, filters.styles, filters.buckets, selectedDate]
  );

  useLayoutEffect(() => {
    if (!isHydrated) return;
    if (search === lastWritten.current) return;

    const isDiscreteChange = discreteKey !== lastDiscreteKey.current;
    lastDiscreteKey.current = discreteKey;

    const write = () => {
      try {
        writeSearchToUrl(search);
        lastWritten.current = search;
      } catch {
        // A blocked replaceState must not break filtering on mobile WebViews.
      }
    };

    if (isDiscreteChange) {
      write();
      return;
    }

    const timer = window.setTimeout(write, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search, discreteKey, isHydrated]);

  return { isHydrated };
}
